/** @jsxImportSource @emotion/react */
"use client";

import { type Interpolation } from "@emotion/react";
import type { Images } from "@packages/sdk";
import { HallowDataTestId } from "@tools/playwright/global";
import type { JSX, ReactNode } from "react";
import { forwardRef, useState } from "react";
import { useFocus, useHover, usePress } from "react-aria";

import type { ThemeType } from "../../../../../../global/emotion";
import {
  constantSemanticColors,
  dropShadow,
  radius,
  shades,
  spacing,
} from "../../../../../../global/emotion";
import { stylesOpacity } from "../../../../../../global/emotion/styles/stylesOpacity";
import { stylesOutline } from "../../../../../../global/emotion/styles/stylesOutline";
import { stylesScale } from "../../../../../../global/emotion/styles/stylesScale";
import type { HallowElement } from "../../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../../utils/determineElementFromAsChild";
import { HallowImage } from "../../../../blocks/other/HallowImage/HallowImage";
import { Skeleton } from "../../../_core/Skeleton/Skeleton.emotion";
import { Text } from "../../../_core/Text/Text.emotion";
import { SubscriptionIcon } from "../../../icons/Icon/GeneralIcons";
import type { IconElement } from "../../../icons/Icon/Icon";
import { InlineIcon } from "../../../icons/Icon/Icon";

const hallowElement: HallowElement = "div";

const descriptionContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
};
const determinedElementStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  minWidth: spacing.none,
  position: "relative",
};
const infoContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
};
const imgStyle: Interpolation = {
  height: spacing.full,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  position: "absolute",
  top: 0,
  width: spacing.full,
};
const imgBlurStyle: Interpolation = {
  filter: `blur(${spacing.ml})`,
  transform: "scale(1.05)",
  backgroundColor: constantSemanticColors.fixedDarkenMed,
};
const imgContainerStyle: Interpolation = {
  borderRadius: radius.s,
  overflow: "hidden",
  paddingBottom: "70%",
  position: "relative",
  width: spacing.full,
};
const metadataContainerStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  color: theme.semanticColors.neutralsMedium,
  display: "flex",
  gap: spacing.xs,
});
const rankStyle: Interpolation = {
  alignItems: "center",
  backgroundColor: constantSemanticColors.onColorHighest,
  borderRadius: radius.xs,
  boxShadow: `${dropShadow.none} ${dropShadow.s} ${dropShadow.ms} ${dropShadow.none} ${shades.shade20}`,
  color: constantSemanticColors.onColorLowest,
  display: "flex",
  height: spacing.xl,
  justifyContent: "center",
  left: spacing.s,
  position: "absolute",
  top: spacing.s,
  width: spacing.xl,
};
const textContainerStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
};
const titleContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.xxs,
};
const subscriptionIconStyle: Interpolation = {
  display: "flex",
  flexShrink: 0,
};

const containerLoadingStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
};
const descriptionLoadingStyle: Interpolation = {
  height: spacing.m,
  width: spacing.full,
};
const imageLoadingStyle: Interpolation = {
  borderRadius: radius.s,
  paddingBottom: "70%",
};
const textContainerLoadingStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xxs,
  paddingLeft: spacing.xxs,
  paddingRight: spacing.xxs,
};
const titleLoadingStyle: Interpolation = {
  height: spacing.l,
  width: "75%",
};

export type ContentMediumProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  activeState?: boolean;
  description?: string;
  focusState?: boolean;
  hoverState?: boolean;
  imgBlur?: boolean;
  imgSrc?: Images;
  loading?: boolean;
  metadata?: ReactNode;
  metadataInlineIcon?: IconElement;
  rank?: number;
  subscription?: boolean;
  title?: string;
  imgOverlay?: ReactNode;
  imgContainerProps?: HallowElementProps<"div">;
};

export const ContentMedium = forwardRef<any, ContentMediumProps>(
  (
    {
      activeState = true,
      asChild = false,
      children,
      description,
      focusState = true,
      hoverState = true,
      imgBlur,
      imgSrc,
      loading = false,
      metadata,
      metadataInlineIcon,
      rank,
      subscription,
      title,
      imgOverlay,
      imgContainerProps = {},
      ...props
    },
    ref,
  ) => {
    const [focus, setFocus] = useState(false);

    const { pressProps: activeProps } = usePress({});
    const { focusProps } = useFocus({
      onFocusChange: (focus) => setFocus(focus),
    });
    const { isHovered: hover, hoverProps } = useHover({});

    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    if (loading)
      return (
        <div css={containerLoadingStyle}>
          <Skeleton css={imageLoadingStyle} />
          <div css={textContainerLoadingStyle}>
            <Skeleton css={titleLoadingStyle} />
            <Skeleton css={descriptionLoadingStyle} />
          </div>
        </div>
      );

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        <div key="imgContainer" css={imgContainerStyle} {...imgContainerProps}>
          <HallowImage
            alt={title}
            src={imgSrc}
            size={"m"}
            css={[
              imgStyle,
              imgBlur && imgBlurStyle,
              stylesOutline.base,
              focus && focusState ? stylesOutline.focusControlled : null,
              hover && hoverState ? stylesOutline.hoverControlled : null,
            ]}
          />
          {imgOverlay ?? null}
          {imgContainerProps.children}
        </div>,
        <div key="infoContainer" css={infoContainerStyle}>
          <div css={textContainerStyle}>
            <div css={titleContainerStyle}>
              {subscription && (
                <InlineIcon
                  icon={<SubscriptionIcon.Small css={subscriptionIconStyle} />}
                />
              )}
              <Text overflow="ellipsis" size="l" type="title">
                {title}
              </Text>
            </div>
            <div css={descriptionContainerStyle}>
              <Text overflow="ellipsis" size="s" type="body">
                {description}
              </Text>
            </div>
          </div>
          {metadata && (
            <div css={metadataContainerStyle}>
              {metadataInlineIcon && <InlineIcon icon={metadataInlineIcon} />}
              <Text overflow="ellipsis" size="l" type="detail">
                {metadata}
              </Text>
            </div>
          )}
        </div>,
        rank ? (
          <span key="rank" css={rankStyle}>
            <Text size="m" type="title">
              {rank}
            </Text>
          </span>
        ) : null,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        data-testid={HallowDataTestId.ContentMedium}
        {...activeProps}
        {...focusProps}
        {...hoverProps}
        css={[
          determinedElementStyle,
          activeState ? stylesOpacity.active : null,
          activeState ? stylesOpacity.base : null,
          activeState && hoverState ? stylesScale.base : null,
          activeState && hoverState ? stylesScale.m : null,
        ]}
        {...props}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

ContentMedium.displayName = "ContentMedium";
